import type { AuthenticationResponse } from './api';

export const PERMISSIONS = {
  dashboardView: 'dashboard.view',
  companiesView: 'companies.view',
  companiesManage: 'companies.manage',
  brandsView: 'brands.view',
  storesView: 'company.stores.view',
  productsView: 'company.products.view',
  productsManage: 'company.products.manage',
  ordersViewCompany: 'company.orders.view',
  ordersViewAll: 'orders.view.all',
  consumersViewAll: 'consumers.view.all',
  usersManageSystem: 'system.users.manage',
  usersManageCompany: 'company.users.manage',
  rolesManageSystem: 'system.roles.manage',
  permissionsManageSystem: 'system.permissions.manage',
  messagesManage: 'messages.manage',
  activityView: 'activity.view',
  loginOptionsManage: 'system.login-options.manage',
  agentsView: 'system.agents.view',
  helpView: 'help.view',
} as const;

const ROLE_PERMISSION_DEFAULTS: Record<string, string[]> = {
  Admin: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.companiesManage,
    PERMISSIONS.companiesView,
    PERMISSIONS.brandsView,
    PERMISSIONS.storesView,
    PERMISSIONS.productsView,
    PERMISSIONS.productsManage,
    PERMISSIONS.ordersViewAll,
    PERMISSIONS.consumersViewAll,
    PERMISSIONS.usersManageSystem,
    PERMISSIONS.rolesManageSystem,
    PERMISSIONS.permissionsManageSystem,
    PERMISSIONS.messagesManage,
    PERMISSIONS.activityView,
    PERMISSIONS.loginOptionsManage,
    PERMISSIONS.agentsView,
    PERMISSIONS.helpView,
  ],
  SuperAdmin: [],
  NearrmeAdmin: [],
  NearrmeUser: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.companiesView,
    PERMISSIONS.brandsView,
    PERMISSIONS.storesView,
    PERMISSIONS.productsView,
    PERMISSIONS.ordersViewAll,
    PERMISSIONS.consumersViewAll,
    PERMISSIONS.messagesManage,
    PERMISSIONS.activityView,
    PERMISSIONS.helpView,
  ],
  CompanyOwner: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.companiesView,
    PERMISSIONS.brandsView,
    PERMISSIONS.storesView,
    PERMISSIONS.productsView,
    PERMISSIONS.productsManage,
    PERMISSIONS.ordersViewCompany,
    PERMISSIONS.usersManageCompany,
    PERMISSIONS.messagesManage,
    PERMISSIONS.activityView,
    PERMISSIONS.helpView,
  ],
  CompanyAdmin: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.companiesView,
    PERMISSIONS.brandsView,
    PERMISSIONS.storesView,
    PERMISSIONS.productsView,
    PERMISSIONS.storesView,
    PERMISSIONS.productsManage,
    PERMISSIONS.ordersViewCompany,
    PERMISSIONS.usersManageCompany,
    PERMISSIONS.messagesManage,
    PERMISSIONS.activityView,
    PERMISSIONS.helpView,
  ],
  CompanyManager: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.brandsView,
    PERMISSIONS.productsView,
    PERMISSIONS.storesView,
    PERMISSIONS.ordersViewCompany,
    PERMISSIONS.activityView,
    PERMISSIONS.helpView,
  ],
  StoreOwner: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.productsView,
    PERMISSIONS.ordersViewCompany,
    PERMISSIONS.activityView,
    PERMISSIONS.helpView,
  ],
  StoreManager: [
    PERMISSIONS.dashboardView,
    PERMISSIONS.productsView,
    PERMISSIONS.ordersViewCompany,
    PERMISSIONS.activityView,
    PERMISSIONS.helpView,
  ],
};

ROLE_PERMISSION_DEFAULTS.SuperAdmin = ROLE_PERMISSION_DEFAULTS.Admin;
ROLE_PERMISSION_DEFAULTS.NearrmeAdmin = ROLE_PERMISSION_DEFAULTS.Admin;

export const ROUTE_POLICIES: Record<string, string[]> = {
  '/admin': [PERMISSIONS.dashboardView],
  '/admin/companies': [PERMISSIONS.companiesView],
  '/admin/brands': [PERMISSIONS.brandsView],
  '/admin/stores': [PERMISSIONS.storesView],
  '/admin/products': [PERMISSIONS.productsView],
  '/admin/orders': [PERMISSIONS.ordersViewCompany],
  '/admin/consumers': [PERMISSIONS.consumersViewAll],
  '/admin/nearrme-team': [PERMISSIONS.usersManageSystem],
  '/admin/company-team': [PERMISSIONS.usersManageCompany],
  '/admin/users': [PERMISSIONS.usersManageCompany],
  '/admin/contact-messages': [PERMISSIONS.messagesManage],
  '/admin/help': [PERMISSIONS.helpView],
  '/admin/agents': [PERMISSIONS.agentsView],
  '/admin/setup/login-options': [PERMISSIONS.loginOptionsManage],
  '/admin/setup/roles': [PERMISSIONS.rolesManageSystem],
  '/admin/setup/permissions': [PERMISSIONS.permissionsManageSystem],
  '/admin/setup/activity': [PERMISSIONS.activityView],
};

export function getUserPermissionCodes(user?: AuthenticationResponse | null): Set<string> {
  const roles = (user?.role ?? '')
    .split(/[,\s;|]+/)
    .map((role) => role.trim())
    .filter(Boolean);

  const codes = new Set<string>();
  roles.forEach((role) => {
    (ROLE_PERMISSION_DEFAULTS[role] ?? []).forEach((code) => codes.add(code));
  });
  return codes;
}

export function hasPermission(user: AuthenticationResponse | null | undefined, permissionCode: string): boolean {
  return getUserPermissionCodes(user).has(permissionCode);
}

export function hasAnyPermission(user: AuthenticationResponse | null | undefined, permissionCodes: string[]): boolean {
  if (permissionCodes.length === 0) return true;
  const userCodes = getUserPermissionCodes(user);
  return permissionCodes.some((code) => userCodes.has(code));
}

export function policyForPath(pathname: string): string[] {
  const match = Object.keys(ROUTE_POLICIES)
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];
  return match ? ROUTE_POLICIES[match] : [];
}
